import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { isNewsletterEnabled, subscribe } from "../lib/newsletter";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-lemon";

const DEFAULT_HEADLINE =
  "Una mail ogni tanto con gadget utili e tip di prep — senza rumore";
const DEFAULT_LEAD = "prep-starter";
const CONSENT_REQUIRED_MSG =
  "Per iscriverti serve il consenso all’informativa privacy.";

export default function NewsletterForm({
  headline = DEFAULT_HEADLINE,
  leadMagnet = DEFAULT_LEAD,
  source = "footer",
  className = "",
}) {
  const statusId = useId();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  if (!isNewsletterEnabled()) return null;

  const consentInvalid = status === "error" && message === CONSENT_REQUIRED_MSG;
  const showStatus = Boolean(message);

  async function onSubmit(e) {
    e.preventDefault();
    if (!consent) {
      setStatus("error");
      setMessage(CONSENT_REQUIRED_MSG);
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      await subscribe({ email, consent, leadMagnet, source });
      setStatus("ok");
      setMessage("Iscrizione registrata. Grazie — a presto in inbox.");
      setEmail("");
      setConsent(false);
    } catch (err) {
      if (err.status === 409) {
        setStatus("duplicate");
        setMessage("Questo indirizzo è già iscritto.");
      } else {
        setStatus("error");
        setMessage(err.message || "Qualcosa è andato storto. Riprova più tardi.");
      }
    }
  }

  return (
    <div
      className={className}
      data-testid="newsletter-form"
      aria-labelledby="newsletter-heading"
    >
      <p
        id="newsletter-heading"
        className="text-[11px] tracking-overline uppercase text-cream/70 mb-3"
      >
        Newsletter
      </p>
      <p className="text-sm text-cream/90 leading-relaxed mb-4 max-w-sm">
        {headline}
      </p>
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <label className="block">
          <span className="sr-only">Email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="la tua email"
            disabled={status === "loading"}
            aria-describedby={showStatus ? statusId : undefined}
            className={`w-full min-h-11 px-3 bg-cream/10 border border-cream/25 text-cream text-sm placeholder:text-cream/50 rounded-sm ${FOCUS}`}
            data-testid="newsletter-email"
          />
        </label>
        <label className="flex gap-3 items-start text-xs text-cream/70 leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (consentInvalid) {
                setStatus("idle");
                setMessage("");
              }
            }}
            disabled={status === "loading"}
            required
            aria-required="true"
            aria-invalid={consentInvalid || undefined}
            aria-describedby={showStatus ? statusId : undefined}
            className={`mt-0.5 h-5 w-5 min-h-5 min-w-5 shrink-0 accent-lemon ${FOCUS}`}
            data-testid="newsletter-consent"
          />
          <span>
            Ho letto l&apos;
            <Link to="/privacy" className={`link-underline rounded-sm ${FOCUS}`}>
              Informativa privacy
            </Link>{" "}
            e acconsento a ricevere email editoriali (anche con link commerciali; vedi{" "}
            <Link to="/disclosure" className={`link-underline rounded-sm ${FOCUS}`}>
              Trasparenza
            </Link>
            ). Posso revocare in qualsiasi momento.
          </span>
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          className={`min-h-11 px-4 text-sm bg-lemon text-navy font-medium rounded-sm disabled:opacity-60 ${FOCUS}`}
          data-testid="newsletter-submit"
        >
          {status === "loading" ? "Invio…" : "Iscriviti"}
        </button>
        {showStatus ? (
          <p
            id={statusId}
            role="status"
            aria-live="polite"
            className="text-xs text-cream/80"
            data-testid="newsletter-status"
          >
            {message}
          </p>
        ) : null}
      </form>
    </div>
  );
}
